'use client';

import NextImage from 'next/image';
import SwapTextButton from '@/components/ui/SwapTextButton/SwapTextButton';
import CmsImage from '@/components/ui/CmsImage';
import { MEDIA_PLACEHOLDER } from '@/lib/media-placeholder';
import type { CmsButtonTarget } from '@/lib/cms-button-target';

export type ProductTypeCardData = {
  id: string;
  title: string;
  tagline: string;
  description: string;
  imageSrc: string;
  imageAlt: string;
  listgroupTitle: string;
  bestFor: string[];
  primaryCtaText: string;
  primaryCtaHref?: string;
  primaryCtaTarget?: CmsButtonTarget;
  downloadHref?: string;
  learnMoreHref?: string;
};

export default function ProductTypeCard({
  title,
  tagline,
  description,
  imageSrc,
  imageAlt,
  listgroupTitle,
  bestFor,
  primaryCtaText,
  primaryCtaHref,
  primaryCtaTarget,
  learnMoreHref,
}: ProductTypeCardData) {
  return (
    <article className="flex h-full flex-col overflow-hidden rounded-xl border border-[#333333] bg-[#111111]">
      <div className="relative aspect-square w-full shrink-0 overflow-hidden rounded-t-xl bg-[#1a1a1a]">
        <CmsImage
          src={imageSrc}
          alt={imageAlt}
          fill
          className="object-cover object-center"
          sizes="(min-width: 1024px) 25vw, (min-width: 640px) 50vw, 100vw"
          fallbackSrc={MEDIA_PLACEHOLDER}
        />
      </div>

      <div className="flex flex-1 flex-col p-5 md:p-6">
        <h3 className="text-xl font-bold text-white md:text-2xl">{title}</h3>
        <p className="mt-1 min-h-[3.5rem] line-clamp-4 text-sm font-normal text-white md:text-sm">
          {tagline}
        </p>
        <p className="mt-3 text-sm leading-relaxed text-white md:text-sm">{description}</p>

        <div className="my-5 border-t border-dotted border-white/25" aria-hidden />

        <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-white">
          {listgroupTitle || 'Best for:'}
        </p>
        <ul className="mt-3 flex flex-1 flex-col gap-2.5">
          {bestFor.map((line) => (
            <li key={line} className="flex gap-2.5 text-sm leading-snug text-white">
              <NextImage
                src="/images/double-check-red.svg"
                alt=""
                width={18}
                height={18}
                className="mt-0.5 h-[18px] w-[18px] shrink-0"
              />
              <span>{line}</span>
            </li>
          ))}
        </ul>

        <div className="mt-6 flex flex-col gap-3">
          <SwapTextButton
            tone="light"
            text={primaryCtaText}
            href={primaryCtaHref}
            target={primaryCtaTarget}
            showArrow
            className="w-full justify-center shadow-md text-xs whitespace-nowrap"
          />
          <div className="flex">
            <SwapTextButton
              variant="outline"
              text="Learn More"
              href={learnMoreHref}
              showArrow={false}
              className="h-11 min-h-0 w-full justify-center !rounded-md !border-[#444444] !bg-[#1a1a1a] !py-0 !text-white !shadow-none hover:!bg-white/10 hover:!text-white"
            />
          </div>
        </div>
      </div>
    </article>
  );
}
